import {
  listJobsResponseSchema,
  jobSchema,
  createJobInputSchema,
  toStandardError,
  type CreateJobInput,
  type Job,
} from "@repo/shared";

import { API_V1_PREFIX, apiRequestData, apiRequestJson } from "../api-client";

export type PaginatedList<T> = {
  data: T[];
  total: number;
  nextCursor?: string;
};

export async function listJobs(params?: {
  limit?: number;
  offset?: number;
  cursor?: string;
  // Comma-separated lists; the API filters on any of the given values.
  type?: string;
  status?: string;
  q?: string;
  dateFrom?: string;
  dateTo?: string;
}): Promise<PaginatedList<Job>> {
  const search = new URLSearchParams();
  if (params?.limit !== undefined) {
    search.set("limit", String(params.limit));
  }
  if (params?.offset) {
    search.set("offset", String(params.offset));
  }
  if (params?.cursor) {
    search.set("cursor", params.cursor);
  }
  if (params?.type) {
    search.set("type", params.type);
  }
  if (params?.status) {
    search.set("status", params.status);
  }
  if (params?.q) {
    search.set("q", params.q);
  }
  if (params?.dateFrom) {
    search.set("dateFrom", params.dateFrom);
  }
  if (params?.dateTo) {
    search.set("dateTo", params.dateTo);
  }

  const path =
    search.size > 0 ? `${API_V1_PREFIX}/jobs?${search.toString()}` : `${API_V1_PREFIX}/jobs`;
  const response = await apiRequestJson({
    path,
    responseSchema: listJobsResponseSchema,
    badResponseMessage: "Unexpected response from server",
  });

  return response;
}

export async function getJob(id: string): Promise<Job> {
  return apiRequestData({
    path: `${API_V1_PREFIX}/jobs/${id}`,
    method: "GET",
    dataSchema: jobSchema,
    badResponseMessage: "Unexpected response from server",
  });
}

export async function createJob(input: CreateJobInput): Promise<Job> {
  const validated = createJobInputSchema.safeParse(input);
  if (!validated.success) {
    throw toStandardError({
      code: "VALIDATION_ERROR",
      message: "Invalid input",
      status: 400,
      details: validated.error.flatten(),
    });
  }

  return apiRequestData({
    path: `${API_V1_PREFIX}/jobs`,
    method: "POST",
    body: validated.data,
    dataSchema: jobSchema,
    badResponseMessage: "Unexpected response from server",
  });
}

export async function retryJob(id: string): Promise<Job> {
  return apiRequestData({
    path: `${API_V1_PREFIX}/jobs/${id}/retry`,
    method: "POST",
    dataSchema: jobSchema,
    badResponseMessage: "Unexpected response from server",
  });
}

export async function cancelJob(id: string): Promise<Job> {
  return apiRequestData({
    path: `${API_V1_PREFIX}/jobs/${id}/cancel`,
    method: "POST",
    dataSchema: jobSchema,
    badResponseMessage: "Unexpected response from server",
  });
}

export type { Job };
